/**
 * 대시보드 전체 새로고침 신호.
 *
 * 전에는 이 composable 이 loadDashboardData() 로 모든 방문자에게 13개 요청을
 * 직접 쏘았다. 그 요청들이 위젯 키가 아니라 재무·신청인 페이지 키로 막혀
 * 있어서, 권한 화면에서 위젯을 끈 사람에게도 요청이 나가 403 이 쌓이고
 * (60초마다), 반대로 위젯을 켜 준 사람은 재무 게이트에 막혀 타일이 깨졌다.
 *
 * 이제 위젯이 각자 자기 엔드포인트를 자기 키로 읽는다. 여기 남은 역할은
 * "지금 다시 읽어라" 를 한 번에 알리는 것뿐이다 — 위젯은 tick 을 지켜보다
 * 자기 것만 다시 읽는다. 끈 위젯은 애초에 그려지지 않으니 요청도 없다.
 */
import { ref, type Ref } from 'vue';

const REFRESH_MS = 60_000;

const tick = ref(0);
const loadedAt = ref<string | null>(null);
let refreshTimer: ReturnType<typeof setInterval> | null = null;

function refresh(): void {
  loadedAt.value = new Date().toISOString();
  tick.value += 1;
}

function startAutoRefresh(): void {
  if (refreshTimer) return;
  refreshTimer = setInterval(refresh, REFRESH_MS);
}

function stopAutoRefresh(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * 세션이 바뀌면(로그아웃·재로그인) 남아 있던 것을 비운다.
 *
 * 모듈 수준 싱글턴이라 화면을 옮겨도 살아 있다 — 앞 사람의 "마지막 새로고침"
 * 시각이 다음 사람 화면에 남는 것을 막는다.
 */
function resetForNewSession(): void {
  stopAutoRefresh();
  loadedAt.value = null;
}

export interface DashboardSummaryHandle {
  /** 전체 새로고침 신호. 위젯이 watch 해서 자기 것만 다시 읽는다. */
  tick: Ref<number>;
  loadedAt: Ref<string | null>;
  refresh: () => void;
  startAutoRefresh: () => void;
  stopAutoRefresh: () => void;
  resetForNewSession: () => void;
}

export function useDashboardSummary(): DashboardSummaryHandle {
  return { tick, loadedAt, refresh, startAutoRefresh, stopAutoRefresh, resetForNewSession };
}
